import { Fragment, useCallback, useContext, useState } from "react";
import SubHeader, { SubHeaderLeft, SubHeaderRight, SubheaderSeparator } from "../../../layout/SubHeader/SubHeader";
import Card, { CardBody, CardTitle } from "../../../components/bootstrap/Card";
import Button from "../../../components/bootstrap/Button";
import { handleConfirmationAlert } from "../../../utils/ConfirmationAlert";
import ErrorMessage from "../../../components/ErrorMessage";
import { CustomTable } from "../../../components/table/CustomTable";
import { toast } from "react-toastify";
import useFetch from "../../../hooks/useFetch";
import useFilters from "../../../hooks/useFilters";
import CreateModalCode from "../code-create/createModal";
import { CodeService } from "../../../services/codes/codeService";
import { CodesApiResponse } from "../../../type/code-type";
import CodeFilters from "./code-options/CodeFilters";
import { PrivilegeContext } from "../../../components/priviledge/PriviledgeProvider";
import Page from "../../../layout/Page/Page";

export interface ICodeFilters {
    active?: number;
}

const codeFilters: ICodeFilters = {
    active: 1
};

const CodeList = () => {

    const { userCan } = useContext(PrivilegeContext);
    const codeService = new CodeService();

    const { filters, updateFilters, resetFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters(codeFilters);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [show, setShow] = useState(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await codeService.listCodes(filters);
        return response.getResponseData() as CodesApiResponse;
    }, [filters]));

    const handleDelete = async (id: string) => {
        try {
            const response = await (await codeService.deleteCode(id)).getResponseData();
            if (response.success) {
                refetch();
                setTimeout(() => {
                    toast.success('Código eliminado correctamente');
                }, 100);
            } else {
                toast.error(response.message || "Error al eliminar el código");
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    const _onCloseModal = (type: number, message?: string, errors?: any) => {
        if (type === 1) {
            setSelectedItem(null);
            setShow(false);
            setModalOpen(false);
            refetch();
            toast.success(message);
        } else if (type === 0) {
            message !== '' && toast.error(message);
            errors && errors.forEach((error: any) => {
                toast.error(error.message);
            });
        }
    };

    const getContent = () => {
        if (error) return <ErrorMessage error={error} />;

        if (data) {
            let apiData = data as CodesApiResponse;

            return (
                <CustomTable
                    title="Códigos"
                    data={apiData ? apiData.codes : null}
                    pagination={true}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: apiData as CodesApiResponse ? apiData.lastPage : 1,
                        handlePagination: (page: any) => {
                            updatePage({ selected: page.selected + 1 });
                        },
                        handlePerPage: updatePageSize,
                    }}
                    className={"table-striped table-hover"}
                    columns={[
                        {
                            name: "Nombre",
                            keyValue: "name",
                            className: "text-center",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <div className="cursor-pointer text-primary fw-bold text-center" onClick={() => {
                                        setSelectedItem(element);
                                        setModalOpen(true);
                                        setShow(true);
                                    }}>
                                        {element.name}
                                    </div>
                                )
                            },

                        },
                        {
                            name: "Acrónimo",
                            keyValue: "acronym",
                            className: "text-center",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <div className="d-flex justify-content-center">
                                        <div className="text-start">
                                            {element.acronym || "-"}
                                        </div>
                                    </div>
                                );
                            },
                        },
                        { name: "Acciones", className: "min-w-100px text-end", isActionCell: true }
                    ]}
                    actions={
                        [
                            {
                                title: "Editar",
                                buttonType: 'icon',
                                icon: "Edit",
                                additionalClasses: `text-primary ${userCan("edit", "budget_code") ? "" : "d-none"}`,
                                description: "Editar código",
                                callback: (item: any) => {
                                    if (!userCan("edit", "budget_code")) return;
                                    setSelectedItem(item);
                                    setModalOpen(true);
                                    setShow(false);
                                },
                            },
                            {
                                title: "Eliminar",
                                buttonType: 'icon',
                                icon: "Delete",
                                additionalClasses: `text-danger ${userCan("delete", "budget_code") ? "" : "d-none"}`,
                                description: "Eliminar código",
                                callback: (item: any) => {
                                    if (!userCan("delete", "budget_code")) return;
                                    handleConfirmationAlert({
                                        title: "Eliminar código",
                                        text: "¿Está seguro que desea eliminar el código?",
                                        icon: "warning",
                                        onConfirm: () => {
                                            handleDelete(item.id);
                                        }
                                    })
                                },
                            },
                            {
                                title: "Sin permisos",
                                buttonType: 'icon',
                                icon: "Block",
                                additionalClasses: `text-danger ${(userCan('edit', 'budget_code') || userCan('delete', 'budget_code')) ? 'd-none' : ''}`,
                                description: "No tiene permisos para editar ni eliminar",
                                callback: (item: any) => { },
                            }
                        ]}
                />
            );
        }
    };

    return (
        <>
            <SubHeader>
                <SubHeaderLeft>
                    <CardTitle>Listado de Códigos</CardTitle>
                    <SubheaderSeparator />
                    <Button
                        color="primary"
                        title="Crear código"
                        icon="Add"
                        isLight
                        className={`${userCan("create", "budget_code") ? "" : "d-none"}`}
                        onClick={() => {
                            if (!userCan("create", "budget_code")) return;
                            setModalOpen(true);
                            setSelectedItem(null);
                            setShow(false);
                        }}
                    />
                </SubHeaderLeft>
                <SubHeaderRight>
                    <CodeFilters filters={filters} updateFilters={updateFilters} updatePageSize={updatePageSize} resetFilters={resetFilters} />
                </SubHeaderRight>
            </SubHeader>

            <Page container="fluid">
                <Card stretch={true}>
                    <CardBody className="table-responsive" isScrollable={true}>
                        <>{getContent()}</>
                    </CardBody>
                </Card>
            </Page>

            {modalOpen && (<CreateModalCode isOpen={modalOpen} setIsOpen={setModalOpen} onClose={_onCloseModal} data={selectedItem} show={show} />)}
        </>
    );
};

export default CodeList;